import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Icon,
    LinearProgress,
    Typography,
} from '@mui/material';

import { FormProvider, useForm } from 'react-hook-form';
import { PRODUCT_UPLOAD_CHUNK_SIZE } from 'shared/constants';
import { useCsvFile } from 'shared/hooks';
import { useProductUpload } from 'shared/mutations';
import { ProductUploadFile, ProductUploadRevision } from 'shared/types';
import { calculateMargin, compareToMarginFloor, DATE_TIME_MIN } from 'shared/utility';
import OverrideUntilField from '../override-until-field';
import RevisionSelectField from '../revision-select-field';

interface UploadModalProps {
    open: boolean;
    onClose: () => void;
}

const ACCEPTED_FIELDS = [
    'ProductCode',
    'ProductCostUnit',
    'MarginFloor',
    'CurrentPrice',
    'RecommendedPrice',
    'RevisedPrice',
    'QuantityBreak2',
    'QuantityBreak3',
    'QuantityBreak4',
    'QuantityBreak5',
];

export default function UploadModal({ open, onClose }: UploadModalProps) {
    const [file, error, inputRef, onFileChange, trigger, clearData] = useCsvFile<ProductUploadFile>(ACCEPTED_FIELDS);
    const { mutation, progress, setTotal, incrementProgress } = useProductUpload();

    const methods = useForm({
        mode: 'onBlur',
        defaultValues: {
            revisionReason: null,
            overrideUntil: DATE_TIME_MIN,
        },
    });

    const {
        watch,
        formState: { errors },
    } = methods;

    const revisionReason = watch('revisionReason');
    const overrideUntil = watch('overrideUntil');

    const productsForAutoApproval = file.data.filter(csv => csv.RevisedPrice === csv.RecommendedPrice);

    const productsWithChanges = file.data.filter(csv => csv.RevisedPrice !== csv.RecommendedPrice);

    const productsBelowMargin = productsWithChanges.filter(csv =>
        compareToMarginFloor(calculateMargin(csv.RevisedPrice, csv.ProductCostUnit), csv.MarginFloor)
    );

    const loading = mutation.isLoading;

    const resultHasApprovals = Boolean(productsForAutoApproval.length);
    const resultHasChanges = Boolean(productsWithChanges.length);
    const changesAreBelowMarginFloor = Boolean(productsBelowMargin.length);
    const hasError = Boolean(error);
    const noFileData = !Boolean(file.data.length);
    const hasChangesButNoReason = resultHasChanges && !Boolean(revisionReason);
    const disableSave = Boolean(hasError || noFileData || hasChangesButNoReason || loading);

    const closeDialog = () => {
        onClose();
        clearData();
    };

    const save = async () => {
        const mappedApprovals = productsForAutoApproval.map<ProductUploadRevision>(product => ({
            ...product,
            RevisionReason: null,
            OverrideUntil: DATE_TIME_MIN,
        }));
        const mappedChanges = productsWithChanges.map<ProductUploadRevision>(product => ({
            ...product,
            RevisionReason: revisionReason,
            OverrideUntil: overrideUntil,
        }));

        const products = [...mappedApprovals, ...mappedChanges];

        await mutation.mutateAsync({ products, progress: { incrementProgress, setTotal } });
        closeDialog();
    };

    return (
        <Dialog fullWidth open={open}>
            <DialogContent sx={{ maxHeight: '300px' }}>
                <FormProvider {...methods}>
                    <DialogContentText>Upload File</DialogContentText>
                    <div>
                        <Button onClick={trigger}>
                            Choose file
                            <Icon>upload_file</Icon>
                        </Button>
                        <input ref={inputRef} onChange={onFileChange} accept='.csv' hidden type='file' />
                        <Typography variant='caption'>{file?.name}</Typography>
                    </div>
                    {error ? (
                        <Typography sx={{ color: 'red' }}>{error}</Typography>
                    ) : (
                        <div>
                            {mutation.isLoading &&
                                (file.data.length < PRODUCT_UPLOAD_CHUNK_SIZE ? (
                                    <CircularProgress variant='indeterminate' />
                                ) : (
                                    <LinearProgress variant='determinate' value={progress} />
                                ))}
                            {resultHasApprovals && (
                                <p>
                                    <strong>{productsForAutoApproval.length}</strong> Products will be marked{' '}
                                    <strong>Approved</strong>
                                </p>
                            )}
                            {resultHasChanges && (
                                <div>
                                    <p>
                                        <strong>{productsWithChanges.length}</strong> Products have revisions. Please
                                        select a reason and Override until date (optional)
                                    </p>
                                    <div>
                                        <RevisionSelectField
                                            name='revisionReason'
                                            label='Select a reason'
                                            rules={{
                                                required: { value: true, message: 'Reason is required' },
                                            }}
                                            error={Boolean(errors.revisionReason)}
                                            helperText={
                                                Boolean(errors.revisionReason) ? errors.revisionReason?.message : ''
                                            }
                                        />
                                        <OverrideUntilField name='overrideUntil' label='Override Until' />
                                    </div>
                                    {changesAreBelowMarginFloor && (
                                        <p>
                                            <strong>{productsBelowMargin.length}</strong> Changed products are below
                                            margin
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button disabled={mutation.isLoading} onClick={closeDialog}>
                    Cancel
                </Button>
                <Button onClick={save} variant='contained' color='primary' disabled={disableSave}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
}
